import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, BookMarked, Calendar, CheckCircle2, Circle, Download, Save, Pencil, Sparkles,
  Upload, FileText, Trash2, Plus, FileUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TopicDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topic, setTopic] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState({});
  const [quiz, setQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showQuizEditor, setShowQuizEditor] = useState(false);

  const isCatechist = user && user.role === "catechist";
  const isAdmin = user && user.role === "admin";
  const isStudent = user && user.role === "student";
  const canEditFormative = isCatechist || isAdmin;

  useEffect(() => {
    api.get(`/topics/${id}`)
      .then((r) => { setTopic(r.data); setDraft(r.data); })
      .catch((e) => {
        if (e.response?.status === 403) {
          toast.error("Este tema no pertenece a tu sacramento asignado");
          navigate("/");
        } else {
          toast.error("No se pudo cargar el tema");
        }
      });
    if (user && user.user_id) {
      api.get(`/progress/me`).then((r) => setCompleted(r.data.completed_topic_ids.includes(id))).catch(() => {});
      api.get(`/quizzes/${id}`).then((r) => {
        if (r.data.questions?.length) {
          setQuiz(r.data);
          setQuizAnswers(new Array(r.data.questions.length).fill(-1));
        } else {
          setQuiz({ topic_id: id, questions: [] });
        }
      }).catch(() => {});
      api.get(`/materials/${id}`).then((r) => setMaterials(r.data)).catch(() => {});
    }
  }, [id, user?.user_id, navigate]);

  const refreshMaterials = async () => {
    try { const { data } = await api.get(`/materials/${id}`); setMaterials(data); } catch {}
  };

  const toggleComplete = async () => {
    if (!user) return toast.info("Inicia sesión para guardar tu progreso");
    await api.post("/progress", { topic_id: id, completed: !completed });
    setCompleted(!completed);
    toast.success(!completed ? "¡Tema completado!" : "Tema marcado como pendiente");
  };

  const saveEdit = async () => {
    try {
      const payload = isAdmin
        ? { title: draft.title, focus: draft.focus, bible_ref: draft.bible_ref, reflection: draft.reflection, unit: draft.unit }
        : { focus: draft.focus, bible_ref: draft.bible_ref, reflection: draft.reflection };
      const r = await api.put(`/topics/${id}`, payload);
      setTopic(r.data); setEditMode(false);
      toast.success("Contenido actualizado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    }
  };

  const submitQuiz = async () => {
    if (!user) return toast.info("Inicia sesión para enviar el quiz");
    if (quizAnswers.includes(-1)) return toast.error("Responde todas las preguntas");
    try {
      const r = await api.post("/quizzes/submit", { topic_id: id, answers: quizAnswers });
      setQuizResult(r.data);
      toast.success(`Resultado: ${r.data.score}/${r.data.total}`);
    } catch (e) {
      toast.error("No se pudo enviar");
    }
  };

  if (!topic) return <div className="container mx-auto p-12">Cargando…</div>;

  const levelLabel = topic.level === "primera_comunion" ? "Primera Comunión" : "Confirmación";
  const backLink = topic.level === "primera_comunion" ? "/primera-comunion" : "/confirmacion";

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-12 py-10 max-w-4xl" data-testid="topic-detail">
      <Link to={backLink} className="inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-primary mb-6" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> Volver a {levelLabel}
      </Link>

      <div className="rounded-2xl bg-card border border-border p-8 md:p-12 shadow-sm">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <Badge className="bg-primary/10 text-primary uppercase text-[10px] tracking-wider">
            {topic.cycle === "encuentro" ? "Ciclo del Encuentro" : topic.cycle === "sacramental" ? "Ciclo Sacramental" : "Ciclo Franciscano"}
          </Badge>
          <span className="flex items-center gap-1 text-xs text-foreground/60">
            <Calendar className="w-3 h-3" /> {topic.date_label}
          </span>
          <span className="text-xs text-secondary font-semibold flex items-center gap-1">
            <BookMarked className="w-3 h-3" /> {topic.bible_ref}
          </span>
        </div>

        {editMode && canEditFormative ? (
          <div className="space-y-4">
            {isAdmin && <Input value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="edit-title" />}
            {isAdmin && <Input value={draft.unit || ""} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} data-testid="edit-unit" />}
            <Input value={draft.bible_ref || ""} onChange={(e) => setDraft({ ...draft, bible_ref: e.target.value })} placeholder="Recurso bíblico" data-testid="edit-ref" />
            <Textarea rows={3} value={draft.focus || ""} onChange={(e) => setDraft({ ...draft, focus: e.target.value })} placeholder="Enfoque" data-testid="edit-focus" />
            <Textarea rows={5} value={draft.reflection || ""} onChange={(e) => setDraft({ ...draft, reflection: e.target.value })} placeholder="Reflexión" data-testid="edit-reflection" />
            <div className="flex gap-3">
              <Button onClick={saveEdit} className="rounded-full" data-testid="save-edit-btn"><Save className="w-4 h-4 mr-1" /> Guardar</Button>
              <Button variant="outline" onClick={() => { setEditMode(false); setDraft(topic); }} className="rounded-full" data-testid="cancel-edit-btn">Cancelar</Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-foreground/60">{topic.unit}</p>
            <h1 className="font-display text-4xl md:text-5xl text-primary mt-2 leading-tight" data-testid="topic-title">{topic.title}</h1>

            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <div className="rounded-xl p-5 bg-muted/40 border border-border">
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-foreground/60 mb-2">Enfoque</p>
                <p className="text-base leading-relaxed">{topic.focus}</p>
              </div>
              <div className="rounded-xl p-5 bg-secondary/10 border border-secondary/30">
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-secondary mb-2">Recurso bíblico</p>
                <p className="font-display text-xl text-primary">{topic.bible_ref}</p>
                <p className="mt-2 text-sm text-foreground/70">Lee el pasaje en familia. Comenta una palabra que te haya tocado el corazón.</p>
              </div>
            </div>

            <div className="mt-8 rounded-xl p-6 border-l-4 border-primary bg-primary/5">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-2">Reflexión</p>
              <p className="font-display text-2xl leading-snug">{topic.reflection}</p>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              {user && (
                <Button onClick={toggleComplete} className="rounded-full" data-testid="complete-topic-btn">
                  {completed ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Circle className="w-4 h-4 mr-2" />}
                  {completed ? "Completado" : "Marcar como completado"}
                </Button>
              )}
              {canEditFormative && (
                <Button variant="secondary" onClick={() => setEditMode(true)} className="rounded-full" data-testid="edit-topic-btn">
                  <Pencil className="w-4 h-4 mr-2" /> Editar
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      {/* GUÍAS PDF */}
      {(isCatechist || isAdmin || isStudent) && (
        <div className="mt-8 rounded-2xl bg-card border border-border p-6 md:p-8" data-testid="pdf-section">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="font-display text-2xl text-primary flex items-center gap-2">
              <FileText className="w-5 h-5" /> Guías PDF
            </h2>
            {(isCatechist || isAdmin) && (
              <PdfUploader
                topicId={id}
                onUploaded={refreshMaterials}
                busy={uploading}
                setBusy={setUploading}
              />
            )}
          </div>
          {materials.length === 0 ? (
            <p className="text-sm text-foreground/60">Aún no hay guías publicadas para este tema.</p>
          ) : (
            <ul className="space-y-2">
              {materials.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3" data-testid={`material-${m.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-foreground truncate">{m.title}</p>
                    <p className="text-xs text-foreground/60">Publicada por {m.author}</p>
                  </div>
                  <a
                    href={`${BACKEND}/api/files/${m.file_id}?auth=${encodeURIComponent(localStorage.getItem("cv2026-token") || "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 px-3 h-9 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
                    data-testid={`dl-${m.id}`}
                  >
                    <Download className="w-4 h-4" /> Descargar guía PDF
                  </a>
                  {(isCatechist || isAdmin) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full text-destructive"
                      data-testid={`del-mat-${m.id}`}
                      onClick={async () => {
                        if (!window.confirm("¿Eliminar esta guía?")) return;
                        try {
                          await api.delete(`/materials/${m.id}`);
                          toast.success("Guía eliminada");
                          refreshMaterials();
                        } catch {
                          toast.error("No se pudo eliminar");
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Estudiante: Entregar evidencias PDF */}
          {isStudent && (
            <div className="mt-6 pt-5 border-t border-border">
              <EvidenceUploader topicId={id} onSubmitted={() => setCompleted(true)} />
            </div>
          )}
        </div>
      )}

      {/* Quiz section */}
      {quiz && (
        <div className="mt-10 rounded-2xl bg-card border border-border p-8" data-testid="quiz-section">
          <div className="flex items-center justify-between gap-2 mb-5">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-secondary" />
              <h2 className="font-display text-3xl text-primary">Mini quiz</h2>
            </div>
            {(isCatechist || isAdmin) && (
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setShowQuizEditor(true)} data-testid="quiz-edit-btn">
                <Pencil className="w-4 h-4 mr-1" /> Editar preguntas
              </Button>
            )}
          </div>

          {quiz.questions.length === 0 ? (
            <p className="text-sm text-foreground/60">Aún no hay preguntas. {(isCatechist || isAdmin) ? "Pulsa “Editar preguntas” para crear el quiz." : "Pídele a tu catequista que prepare el mini quiz."}</p>
          ) : (
            <>
              {quiz.questions.map((q, i) => (
                <div key={i} className="mb-5" data-testid={`q-${i}`}>
                  <p className="font-semibold mb-2">{i + 1}. {q.q}</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {q.options.map((opt, j) => {
                      const isCorrect = quizResult && quizResult.correct_answers[i] === j;
                      const isUserChoice = quizAnswers[i] === j;
                      return (
                        <button
                          key={j}
                          disabled={!!quizResult || !isStudent}
                          onClick={() => { const c = [...quizAnswers]; c[i] = j; setQuizAnswers(c); }}
                          data-testid={`q-${i}-opt-${j}`}
                          className={`text-left p-3 rounded-xl border transition-all ${
                            quizResult ? (isCorrect ? "border-primary bg-primary/10" : isUserChoice ? "border-destructive bg-destructive/10" : "border-border")
                            : isUserChoice ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                          }`}
                        >
                          <span className="text-xs font-bold text-foreground/50 mr-2">{String.fromCharCode(65 + j)}.</span>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {isStudent && (
                !quizResult ? (
                  <Button onClick={submitQuiz} className="rounded-full mt-3" data-testid="quiz-submit">Enviar respuestas</Button>
                ) : (
                  <div className="mt-5 rounded-xl p-5 bg-primary text-primary-foreground">
                    <p className="font-display text-2xl">Resultado: {quizResult.score} / {quizResult.total}</p>
                    <p className="text-sm opacity-80 mt-1">{quizResult.score === quizResult.total ? "¡Excelente! Has comprendido muy bien." : "¡Sigue aprendiendo! Revisa la reflexión y vuelve a intentarlo."}</p>
                  </div>
                )
              )}
            </>
          )}
        </div>
      )}

      {/* Quiz editor (catechist/admin) */}
      <QuizEditor
        open={showQuizEditor}
        onClose={() => setShowQuizEditor(false)}
        topicId={id}
        onSaved={async () => {
          setShowQuizEditor(false);
          try {
            const r = await api.get(`/quizzes/${id}`);
            setQuiz(r.data);
            setQuizAnswers(new Array(r.data.questions?.length || 0).fill(-1));
            setQuizResult(null);
          } catch {}
        }}
      />
    </div>
  );
}

function PdfUploader({ topicId, onUploaded, busy, setBusy }) {
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.includes("pdf") && !file.name.toLowerCase().endsWith(".pdf")) {
      return toast.error("Sube un archivo PDF");
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/materials", { topic_id: topicId, title: file.name, file_id: up.file_id });
      toast.success("Guía PDF publicada");
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity">
      <Upload className="w-4 h-4" />
      {busy ? "Subiendo…" : "Agregar guía PDF"}
      <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={onPick} data-testid="pdf-upload" />
    </label>
  );
}

function EvidenceUploader({ topicId, onSubmitted }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.includes("pdf") && !file.name.toLowerCase().endsWith(".pdf")) {
      return toast.error("La evidencia debe ser un PDF");
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/submissions", { topic_id: topicId, note: note || file.name, file_id: up.file_id });
      toast.success("Evidencia enviada al catequista");
      setNote("");
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3" data-testid="evidence-block">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Entrega tus evidencias</p>
      <Input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Nota opcional para tu catequista"
        data-testid="evidence-note"
      />
      <label className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-accent text-accent-foreground text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity">
        <FileUp className="w-4 h-4" />
        {busy ? "Enviando…" : "Entregar evidencias en PDF"}
        <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={onPick} data-testid="evidence-upload" />
      </label>
      <p className="text-xs text-foreground/60">Tu envío llegará automáticamente a “Revisar tareas” del catequista.</p>
    </div>
  );
}

function QuizEditor({ open, onClose, topicId, onSaved }) {
  const [questions, setQuestions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/quizzes/${topicId}/full`).then((r) => {
      setQuestions(r.data.questions?.length ? r.data.questions.map((q) => ({ q: q.q, options: [...q.options], correct: q.correct })) : []);
    }).catch(() => setQuestions([]));
  }, [open, topicId]);

  const addQ = () => setQuestions((qs) => [...qs, { q: "", options: ["", ""], correct: 0 }]);
  const removeQ = (i) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));
  const setQ = (i, patch) => setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const addOpt = (i) => setQ(i, { options: [...questions[i].options, ""] });
  const removeOpt = (i, j) => {
    const opts = questions[i].options.filter((_, idx) => idx !== j);
    const correct = questions[i].correct >= opts.length ? 0 : questions[i].correct;
    setQ(i, { options: opts, correct });
  };
  const setOpt = (i, j, v) => setQ(i, { options: questions[i].options.map((o, idx) => (idx === j ? v : o)) });

  const save = async () => {
    if (questions.length === 0) return toast.error("Agrega al menos una pregunta");
    for (const [i, q] of questions.entries()) {
      if (!q.q.trim()) return toast.error(`Pregunta ${i + 1}: sin texto`);
      if (q.options.length < 2) return toast.error(`Pregunta ${i + 1}: agrega al menos 2 opciones`);
      if (q.options.some((o) => !o.trim())) return toast.error(`Pregunta ${i + 1}: hay opciones vacías`);
    }
    setBusy(true);
    try {
      await api.put(`/quizzes/${topicId}`, { topic_id: topicId, questions });
      toast.success("Mini quiz actualizado");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="quiz-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-primary">Editar mini quiz</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {questions.length === 0 && (
            <p className="text-sm text-foreground/60">Aún no hay preguntas. Pulsa “Agregar pregunta”.</p>
          )}
          {questions.map((q, i) => (
            <div key={i} className="rounded-xl border border-border p-4 space-y-3" data-testid={`qe-q-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Pregunta {i + 1}</span>
                <Button variant="ghost" size="icon" className="rounded-full text-destructive" onClick={() => removeQ(i)} data-testid={`qe-del-q-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <Textarea
                rows={2}
                value={q.q}
                onChange={(e) => setQ(i, { q: e.target.value })}
                placeholder="Escribe la pregunta"
                data-testid={`qe-q-text-${i}`}
              />
              <div className="space-y-2">
                {q.options.map((opt, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct-${i}`}
                      checked={q.correct === j}
                      onChange={() => setQ(i, { correct: j })}
                      className="w-4 h-4 accent-primary"
                      title="Marcar como respuesta correcta"
                      data-testid={`qe-correct-${i}-${j}`}
                    />
                    <Input
                      value={opt}
                      onChange={(e) => setOpt(i, j, e.target.value)}
                      placeholder={`Opción ${String.fromCharCode(65 + j)}`}
                      data-testid={`qe-opt-${i}-${j}`}
                    />
                    {q.options.length > 2 && (
                      <Button variant="ghost" size="icon" className="rounded-full" onClick={() => removeOpt(i, j)} data-testid={`qe-del-opt-${i}-${j}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => addOpt(i)} data-testid={`qe-add-opt-${i}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Opción
                </Button>
              </div>
              <p className="text-xs text-foreground/60">Marca con el círculo la respuesta correcta.</p>
            </div>
          ))}
          <Button variant="outline" className="rounded-full" onClick={addQ} data-testid="qe-add-q">
            <Plus className="w-4 h-4 mr-1" /> Agregar pregunta
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Cancelar</Button>
          <Button className="rounded-full" disabled={busy} onClick={save} data-testid="qe-save">
            <Save className="w-4 h-4 mr-1" /> {busy ? "Guardando…" : "Guardar mini quiz"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
